import { Component, ErrorInfo, ReactNode } from 'react'
import debugLogger from '../lib/debugLogger'

/**
 * Props for the ErrorBoundary component
 * Allows customization of fallback UI and error handling behavior
 */
interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  resetKeys?: Array<string | number>
}

/**
 * State for tracking error information
 */
interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * ErrorBoundary Component
 * React error boundary that catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the whole app
 * 
 * This is crucial for making the app resilient to errors like invalid PublicKeys,
 * network failures, or other runtime errors
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  /**
   * Static method called when an error is thrown in a child component
   * Updates state to trigger fallback UI rendering
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null
    }
  }

  /**
   * Lifecycle method called after an error has been thrown
   * Logs error details and calls optional error handler
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    debugLogger.error('ErrorBoundary', '🚨 Caught error in component tree', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    })

    // Update state with detailed error information
    this.setState({
      errorInfo
    })

    // Call optional error handler prop
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  /**
   * Reset the error boundary state
   * Allows users to try recovering from the error
   */
  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
  }

  /**
   * Check if resetKeys have changed to auto-reset the boundary
   */
  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys } = this.props
    const { hasError } = this.state

    if (
      hasError &&
      resetKeys &&
      prevProps.resetKeys &&
      !resetKeys.every((key, index) => key === prevProps.resetKeys![index])
    ) {
      this.resetErrorBoundary()
    }
  }

  render() {
    const { hasError, error, errorInfo } = this.state
    const { children, fallback } = this.props

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-dark-bg via-dark-card to-dark-bg flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <div className="bg-dark-card border border-status-error/30 rounded-lg p-8 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-status-error/20 flex items-center justify-center">
                  <svg 
                    className="w-6 h-6 text-status-error" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                    />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-text-primary">
                    Application Error
                  </h1>
                  <p className="text-sm text-text-secondary">
                    Something went wrong in this part of the app
                  </p>
                </div>
              </div>

              {error && (
                <div className="mb-6">
                  <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-2">
                    Error Message
                  </h2>
                  <div className="bg-dark-bg rounded p-4 border border-status-error/20">
                    <code className="text-sm text-status-error font-mono break-words">
                      {error.message}
                    </code>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={this.resetErrorBoundary}
                  className="flex-1 px-6 py-3 bg-cyber-cyan hover:bg-cyber-cyan/80 text-dark-bg font-semibold rounded-lg transition-colors duration-200"
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.location.href = '/'}
                  className="flex-1 px-6 py-3 bg-dark-bg hover:bg-dark-bg/80 text-text-primary font-semibold rounded-lg border border-cyber-cyan/30 transition-colors duration-200"
                >
                  Go Home
                </button>
              </div>

              {/* Debug information - collapsible */}
              {process.env.NODE_ENV === 'development' && errorInfo && (
                <details className="mt-6">
                  <summary className="cursor-pointer text-sm text-text-secondary hover:text-text-primary transition-colors">
                    Show Debug Information
                  </summary>
                  <div className="mt-3 bg-dark-bg rounded p-4 border border-status-warning/20">
                    <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                      Component Stack
                    </h3>
                    <pre className="text-xs text-text-secondary font-mono whitespace-pre-wrap overflow-auto max-h-60">
                      {errorInfo.componentStack}
                    </pre>
                    {error?.stack && (
                      <>
                        <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2 mt-4">
                          Error Stack
                        </h3>
                        <pre className="text-xs text-text-secondary font-mono whitespace-pre-wrap overflow-auto max-h-60">
                          {error.stack}
                        </pre>
                      </>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      )
    }

    return children
  }
}

export default ErrorBoundary